import { FEATURE_FLAGS } from "@cede/types";
import { isServerApiError, isServerApiRfcError, QueryKeys } from "@cede/utils";
import { useFeatureFlagIsOn } from "@/hooks/feature-flags";
import { useHubUser } from "@/hooks/user/useHubUser";
import { getAutopilotPriorityTicketBalance } from "@/utils/api";
import { useQuery } from "@tanstack/react-query";

/**
 * The user's unconsumed autopilot priority ticket balance — earned when a paid
 * Arena competition the user played finalizes, spendable on any autopilot queue
 * to buy prioritized epochs.
 *
 * Gated on the `:api:` priority-tickets flag: when off the query never fires and
 * the balance reads 0, which hides every earn/spend surface (boost button,
 * header chip, profile card). Queue rows with an already-credited
 * `priorityCounter` keep working server-side — only ticket UI is dark.
 */
export const usePriorityTickets = (options?: { enabled?: boolean }) => {
  const { auth, hubUser, authenticated } = useHubUser();
  const isFeatureEnabled = useFeatureFlagIsOn(FEATURE_FLAGS.AUTOMATIC_STREAK_API_PRIORITY_TICKETS_ENABLE);

  const query = useQuery({
    // Keyed by hubUser.id: defiAddress is null for email-only users, so it
    // would reuse one cache entry across account switches.
    queryKey: [QueryKeys.AUTOMATIC_STREAK, "priority-tickets", hubUser?.id],
    queryFn: async () => {
      const result = await getAutopilotPriorityTicketBalance(auth);

      if (isServerApiRfcError(result)) {
        throw new Error(result.detail);
      }
      if (isServerApiError(result)) {
        throw new Error(result.message);
      }

      return result;
    },
    enabled: isFeatureEnabled && authenticated && (options?.enabled ?? true),
    staleTime: 30_000,
  });

  return {
    isFeatureEnabled,
    // 0 while errored — consumers must check `isError` before rendering the
    // number, or a failed request reads as an empty balance.
    totalTickets: isFeatureEnabled ? (query.data?.totalTickets ?? 0) : 0,
    // A disabled-by-flag query stays "pending" forever; report it as settled.
    isLoading: isFeatureEnabled && query.isLoading,
    isError: query.isError,
    error: query.error?.message,
  };
};
